import React from "react";
import { cn } from "@/lib/utils";

interface SummaryCardsProps {
  formValues: Record<string, any>;
  icon?: React.ElementType;
  description?: string;
  className?: string;
}

export function SummaryCards({ formValues, icon: Icon, description, className }: SummaryCardsProps) {
  // Filter and format form values for display
  const displayValues = Object.entries(formValues)
    .filter(([key, value]) => value !== null && value !== undefined && value !== '')
    .slice(0, 6);

  const formatLabel = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase());
  };

  const formatValue = (value: any): string => {
    if (typeof value === 'boolean') {
      return value ? 'Ja' : 'Nein';
    }
    if (typeof value === 'number') {
      return value.toLocaleString('de-DE');
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return String(value);
  };

  return (
    <div className={cn("space-y-6", className)}>
      {/* Hero Section */}
      <div className="h-32 bg-gradient-to-br from-primary/20 via-primary/10 to-primary/5 rounded-xl flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-transparent to-background/50" />
        {Icon && <Icon className="h-16 w-16 text-primary/60 relative z-10" />}
      </div>

      {/* Description */}
      {description && (
        <p className="text-center text-muted-foreground text-sm">
          {description}
        </p>
      )}

      {/* Data Cards Grid */}
      {displayValues.length > 0 ? (
        <div className="grid grid-cols-2 gap-3">
          {displayValues.map(([key, value], index) => (
            <div 
              key={key}
              className={cn(
                "p-3 rounded-lg transition-all animate-in fade-in slide-in-from-bottom-2",
                index % 2 === 0 ? "bg-primary/5" : "bg-muted/50"
              )}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <p className="text-xs text-muted-foreground truncate">
                {formatLabel(key)}
              </p>
              <p className="font-medium truncate mt-0.5">
                {formatValue(value)}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center text-muted-foreground text-sm py-8">
          Erfassen Sie Daten im Berater-Panel
        </div>
      )}
    </div>
  );
}
